import { filterAnyCollateralPayableTokens } from '@ui/config/brand';
import { useBrand } from '@ui/config/BrandProvider';
import { SUPPORTED_CHAIN_IDS } from '@ui/constants/chain';
import { getPayablePoolTokens } from '@ui/constants/tokens';
import { useDataChainStore } from '@ui/hooks/datachain';
import { useHighestValueToken } from '@ui/hooks/useHighestValueToken';
import { useIsMounted } from '@ui/hooks/useIsMounted';
import { ChainId } from '@ui/types/chain';
import { PayablePoolToken } from '@ui/types/token';
import { getLocalStorageKey } from '@ui/utils/getLocalStorageKey';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CollateralTokenStore = {
  collateralTokenMap: Record<ChainId, PayablePoolToken>;
  useCollateralTokenValue: () => PayablePoolToken | undefined;
  setCollateralToken: (token: PayablePoolToken, chainId: ChainId) => void;
};
const defaultMap = Object.fromEntries(
  SUPPORTED_CHAIN_IDS.map((id) => [id, getPayablePoolTokens(id)[0]]),
) as Record<ChainId, PayablePoolToken>;

export const useCollateralToken = create<CollateralTokenStore>()(
  persist(
    (set, get) => ({
      collateralTokenMap: defaultMap,
      useCollateralTokenValue: () => {
        const dataChainId = useDataChainStore((s) => s.dataChain);
        const isMount = useIsMounted();
        const brand = useBrand();
        // const highestValueToken = useHighestValueToken();

        if (isMount) {
          const currentChainPayablePoolTokens = filterAnyCollateralPayableTokens(
            getPayablePoolTokens(dataChainId),
            brand,
          );

          const storedToken = get().collateralTokenMap[dataChainId];

          const isStoredTokenValid = currentChainPayablePoolTokens.some(
            (token) =>
              token.address.toLowerCase() === storedToken.address.toLowerCase(),
          );

          // If stored token is valid, use it
          if (isStoredTokenValid) {
            return get().collateralTokenMap[dataChainId];
          }

          // If user has tokens with value, use the highest value token
          // if (highestValueToken) {
          //   return highestValueToken;
          // }

          // Fallback to default token (USDC)
          return currentChainPayablePoolTokens[0] ?? defaultMap[dataChainId];
        }
      },
      setCollateralToken: (tradingToken, chainId) => {
        set({
          collateralTokenMap: {
            ...get().collateralTokenMap,
            [chainId]: tradingToken,
          },
        });
      },
    }),
    {
      version: 2,
      name: getLocalStorageKey('trade.collateralToken'),
    },
  ),
);
